import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { MapPin, Phone, ShoppingBag, UtensilsCrossed, Search, ClipboardCheck } from "lucide-react";
import { getTrackedOrders, trackUrl } from "@/lib/orderTracking";
import { api, apiError, money, fileUrl } from "@/lib/api";
import { CartProvider, useCart } from "@/context/CartContext";
import { Loader } from "@/components/common/Primitives";
import { Input } from "@/components/ui/input";
import { ProductSheet } from "./ProductSheet";
import { CartDrawer } from "./CartDrawer";

export const img = (u) => (u?.startsWith("/api") ? fileUrl(u) : u);
export const effPrice = (p) => (p.sale_price != null && p.sale_price < p.base_price ? p.sale_price : p.base_price);

function MenuBody({ menu, tableId }) {
  const { count, estimate } = useCart();
  const [active, setActive] = useState(menu.categories[0]?.id);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const r = menu.restaurant;
  const tracked = useMemo(() => getTrackedOrders(r.slug), [r.slug]);

  const grouped = useMemo(() => menu.categories.map((c) => ({
    ...c, items: menu.products.filter((p) => p.category_id === c.id && p.name.toLowerCase().includes(q.toLowerCase())),
  })).filter((c) => c.items.length), [menu, q]);

  const jump = (id) => { setActive(id); document.getElementById(`cat-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div className="min-h-screen bg-[#FAFAF7] pb-28">
      <header className="bg-white">
        <div className="mx-auto max-w-3xl px-5 pb-5 pt-8">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-emerald-50">
              {r.logo_url ? <img src={img(r.logo_url)} alt={r.name} className="h-full w-full object-cover" /> : <UtensilsCrossed className="h-7 w-7 text-emerald-500" />}
            </div>
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-bold text-slate-900 sm:text-3xl" data-testid="menu-restaurant-name">{r.name}</h1>
              <p className="text-sm text-slate-500">{r.cuisine}</p>
            </div>
          </div>
          {r.description && <p className="mt-4 text-sm text-slate-600">{r.description}</p>}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
            {r.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{r.address}</span>}
            {r.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{r.phone}</span>}
          </div>
          {menu.table && <div className="mt-4 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700" data-testid="menu-table-badge">Ordering for {menu.table.name}</div>}
          {tracked.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="track-orders">
              {tracked.slice(0, 3).map((o) => (
                <Link key={o.id} to={trackUrl(o)} data-testid={`track-order-${o.number}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-3 py-1 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-50">
                  <ClipboardCheck className="h-4 w-4" />Track your order {o.number}
                </Link>
              ))}
            </div>
          )}
        </div>
      </header>

      <div className="sticky top-0 z-20 border-b border-slate-100 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-5 py-3">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input className="rounded-full pl-9" placeholder="Search dishes" value={q} onChange={(e) => setQ(e.target.value)} data-testid="menu-search" />
          </div>
          <div className="sm-scroll flex gap-2 overflow-x-auto">
            {grouped.map((c) => (
              <button key={c.id} onClick={() => jump(c.id)} data-testid={`menu-cat-${c.id}`}
                className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${active === c.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{c.name}</button>
            ))}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-5">
        {grouped.length === 0 && <p className="py-16 text-center text-sm text-slate-400" data-testid="menu-empty">No dishes found.</p>}
        {grouped.map((c) => (
          <section key={c.id} id={`cat-${c.id}`} className="scroll-mt-32 pt-8">
            <h2 className="mb-3 font-heading text-lg font-semibold text-slate-900">{c.name}</h2>
            <div className="space-y-3">
              {c.items.map((p) => (
                <button key={p.id} disabled={!p.is_available} onClick={() => setPick(p)} data-testid={`menu-product-${p.id}`}
                  className="flex w-full gap-4 rounded-2xl bg-white p-3 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                  <div className="min-w-0 flex-1 py-1">
                    <p className="font-heading font-semibold text-slate-900">{p.name}</p>
                    {p.description && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{p.description}</p>}
                    <p className="mt-2 font-semibold text-slate-800">
                      {p.sizes?.length ? `From ${money(Math.min(...p.sizes.map((s) => s.price)))}` : money(effPrice(p))}{!p.sizes?.length && effPrice(p) < p.base_price && <s className="ml-2 text-xs font-normal text-slate-400">{money(p.base_price)}</s>}
                      {!p.is_available && <span className="ml-2 text-xs font-medium text-rose-500">Sold out</span>}
                    </p>
                  </div>
                  {p.image_url && <img src={img(p.image_url)} alt={p.name} className="h-24 w-24 shrink-0 rounded-xl object-cover" />}
                </button>
              ))}
            </div>
          </section>
        ))}
      </main>

      {count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 p-4">
          <button onClick={() => setCartOpen(true)} data-testid="cart-open-btn"
            className="sm-fade-up mx-auto flex w-full max-w-3xl items-center justify-between rounded-2xl bg-slate-900 px-5 py-4 text-white shadow-xl">
            <span className="flex items-center gap-2 font-medium"><ShoppingBag className="h-5 w-5" />{count} item{count > 1 && "s"}</span>
            <span className="font-heading font-semibold" data-testid="cart-bar-total">{money(estimate)} · View cart</span>
          </button>
        </div>
      )}
      {pick && <ProductSheet product={pick} menu={menu} onClose={() => setPick(null)} />}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} menu={menu} tableId={tableId} />
    </div>
  );
}

export default function PublicMenu() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const table = params.get("table") || "";
  const [menu, setMenu] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/menu/${slug}`, { params: { table } }).then((r) => setMenu(r.data)).catch((e) => setErr(apiError(e, "Menu not available")));
  }, [slug, table]);

  if (err) return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="menu-error">
      <UtensilsCrossed className="h-10 w-10 text-slate-300" />
      <p className="mt-4 font-heading text-lg font-semibold text-slate-800">{err}</p>
    </div>
  );
  if (!menu) return <Loader label="Loading menu…" />;
  return <CartProvider slug={slug}><MenuBody menu={menu} tableId={table} /></CartProvider>;
}
